import React from "react";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { Box, Grid, Stat, StatLabel, StatNumber, Text } from "@chakra-ui/react";
import type { AdminStats } from "@/api/admin/applications";

type ActiveBlock = "accounts" | "applications" | "pending" | "approved" | "rejected" | null;

// The stats grid is the only intentional bridge between /admin and
// /admin/accounts. Each block links to a target page or filtered view, and
// outlines when its target matches the current URL.
//
// Active-block determination is URL-driven so deep links (e.g. /admin?status[]=pending)
// land with the right block outlined regardless of how the user got there.
const getActiveBlock = (pathname: string, searchParams: URLSearchParams): ActiveBlock => {
  if (pathname.endsWith("/admin/accounts")) return "accounts";
  if (pathname.endsWith("/admin")) {
    // Check both `status[]=` and `status=` since axios serializes arrays
    // as `status[]=` but hand-rolled URLs may use plain `status=`.
    const statuses = [...searchParams.getAll("status[]"), ...searchParams.getAll("status")];
    if (statuses.length === 0) return "applications";
    if (statuses.length === 1 && ["pending", "approved", "rejected"].includes(statuses[0])) {
      return statuses[0] as ActiveBlock;
    }
    // Multiple statuses selected — no single block matches.
    return null;
  }
  return null;
};

interface BlockProps {
  to: string;
  active: boolean;
  children: React.ReactNode;
  background?: string;
}

const ClickableStat = ({ to, active, children, background }: BlockProps) => (
  <Box
    as={RouterLink}
    to={to}
    display="block"
    padding={4}
    borderRadius="lg"
    backgroundColor={background}
    boxShadow={active ? "0 0 0 2px var(--chakra-colors-blue-500)" : "none"}
    transition="box-shadow 0.15s, background-color 0.15s"
    _hover={{ backgroundColor: background ?? "gray.50" }}
  >
    {children}
  </Box>
);

export interface AdminHeaderProps {
  stats?: AdminStats;
}

export const AdminHeader = ({ stats }: AdminHeaderProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const active = getActiveBlock(location.pathname, searchParams);

  return (
    <Grid
      templateColumns={{
        base: "1fr",
        sm: "1fr 1fr",
        md: "1fr max-content 1fr max-content 1fr",
      }}
      gap={6}
      marginBottom={8}
      borderBottom="1px solid"
      borderColor="gray.200"
      alignItems="center"
      paddingBottom={8}
    >
      <ClickableStat to="/admin/accounts" active={active === "accounts"}>
        <Stat textAlign="center">
          <StatNumber fontSize="5xl" lineHeight="1">
            {stats?.total_accounts ?? 0}
          </StatNumber>
          <StatLabel>Accounts</StatLabel>
        </Stat>
      </ClickableStat>
      <Text fontSize="2xl">&rarr;</Text>
      <ClickableStat to="/admin" active={active === "applications"}>
        <Stat textAlign="center">
          <StatNumber fontSize="5xl" lineHeight="1">
            {stats?.total_applications ?? 0}
          </StatNumber>
          <StatLabel>Applications</StatLabel>
        </Stat>
      </ClickableStat>
      <Text fontSize="2xl">=</Text>
      <Grid templateColumns="1fr 1fr 1fr" gap={4}>
        <ClickableStat
          to="/admin?status[]=pending"
          active={active === "pending"}
          background="yellow.50"
        >
          <Stat textAlign="center">
            <StatNumber fontSize="3xl" lineHeight="1" color="yellow.600">
              {stats?.pending ?? 0}
            </StatNumber>
            <StatLabel>Pending</StatLabel>
          </Stat>
        </ClickableStat>
        <ClickableStat
          to="/admin?status[]=approved"
          active={active === "approved"}
          background="green.50"
        >
          <Stat textAlign="center">
            <StatNumber fontSize="3xl" lineHeight="1" color="green.600">
              {stats?.approved ?? 0}
            </StatNumber>
            <StatLabel>Approved</StatLabel>
          </Stat>
        </ClickableStat>
        <ClickableStat
          to="/admin?status[]=rejected"
          active={active === "rejected"}
          background="red.50"
        >
          <Stat textAlign="center">
            <StatNumber fontSize="3xl" lineHeight="1" color="red.600">
              {stats?.rejected ?? 0}
            </StatNumber>
            <StatLabel>Rejected</StatLabel>
          </Stat>
        </ClickableStat>
      </Grid>
    </Grid>
  );
};
